import { AlertProps as MuiAlertProps } from '@mui/material/Alert';
import {
  AnyAction,
  Dispatch,
  isRejectedWithValue,
  Middleware,
  MiddlewareAPI,
} from '@reduxjs/toolkit';

import commonConstants from '@Constants/common.constants';
import TokenService from '@Services/token.service';
import { logout } from '@Slices/auth.slice';
import { showAlert } from '@Slices/globalAlert.slice';
import { CustomError, ErrorData } from '@Typings/index';
/**
 * Log a warning and show a toast!
 */
export const rtkQueryErrorLogger: Middleware =
  (api: MiddlewareAPI) =>
  (next: Dispatch<AnyAction>) =>
  (action: AnyAction) => {
    // RTK Query uses `createAsyncThunk` from redux-toolkit under the hood, so we're able to utilize these use matchers!
    if (isRejectedWithValue(action)) {
      const payload = <ErrorData>action.payload;
      const error: CustomError = {
        message: payload.data?.message
          ? payload.data.message
          : action.error.message,
        status: +payload.status,
        errorPayload: action.error,
        errorData: payload,
        severity: 'error',
      };

      // change the severity based on the error status code
      let errorSeverity: MuiAlertProps['severity'];
      if (payload.status !== 'FETCH_ERROR') {
        // rule out any kind of PARSE_ERROR - 404
        if (payload.status === 'PARSING_ERROR' && !payload?.originalStatus) {
          error.message = payload.status;
          api.dispatch(showAlert(error));
        } else {
          const refreshToken = TokenService.getRefreshToken();
          let status;
          if (error.status && !Number.isNaN(error.status)) {
            status = error.status;
          } else if (
            payload.originalStatus &&
            !Number.isNaN(payload.originalStatus)
          ) {
            status = payload.originalStatus;
          }
          switch (status) {
            case 400:
              // each module will handle for itself
              break;
            case 402:
            case 403:
            case 404:
            case 500:
              break;

            case 401:
              // Token not found - both access and refresh tokens are not present
              if (
                (!refreshToken && error.message === 'TOKEN_NOT_FOUND') ||
                error.message === 'Rejected'
              ) {
                api.dispatch(showAlert(error));
                api.dispatch<{
                  payload: undefined;
                  type: string;
                }>(logout());
              }
              break;

            default:
              errorSeverity = 'info';
              break;
          }
        }
        error.severity = errorSeverity;
      } else {
        // CORS & No Internet shows the generic error
        error.message = commonConstants.GLOBAL_ERROR_CODES.FETCH_ERROR;
        api.dispatch(showAlert(error));
      }
      // Sentry can be logged here
    }
    return next(action);
  };
